/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import classNames from 'classnames';
import PropTypes from "prop-types";
import React, { useRef } from 'react';
import { EuiButtonDisplay } from '../button';
import { useInnerText } from '../../inner_text';
import { htmlIdGenerator } from '../../../services';
export var EuiButtonGroupButton = ({
  className,
  id,
  isDisabled,
  isIconOnly,
  isSelected = false,
  label,
  name,
  onChange,
  size,
  value,
  element = 'button',
  type = 'button',
  ...rest
}) => {
  // Force element to be a button if disabled
  const el = isDisabled ? 'button' : element;
  const newId = useRef(htmlIdGenerator()()).current;
  let elementProps = {};
  let singleInput;

  if (el === 'label') {
    elementProps = { ...elementProps,
      htmlFor: newId,
      onClick: () => onChange(id, value)
    };
    singleInput = <input id={newId} className="euiScreenReaderOnly" name={name} checked={isSelected} disabled={isDisabled} value={value} type="radio" onChange={() => onChange(id, value)} data-test-subj={id} />;
  } else {
    elementProps = { ...elementProps,
      id: newId,
      'data-test-subj': id,
      isSelected,
      type,
      onClick: () => onChange(id)
    };
  }

  const buttonClasses = classNames({
    'euiButtonGroupButton-isSelected': isSelected,
    'euiButtonGroupButton-isIconOnly': isIconOnly
  }, className);
  /**
   * Because the selected buttons also increase their text weight to 'bold',
   * we don't want the whole button size to shift when selected, so we determine
   * the base width of the button via the `euiTextShift()` method in SASS.
   */

  const [buttonTextRef, innerText] = useInnerText();
  return <EuiButtonDisplay baseClassName="euiButtonGroupButton" className={buttonClasses} element={el} fill={size !== 'compressed' && isSelected} isDisabled={isDisabled} size={size === 'compressed' ? 's' : size} textProps={{
    className: isIconOnly ? 'euiScreenReaderOnly' : 'euiButtonGroupButton__textShift',
    ref: buttonTextRef,
    'data-text': innerText,
    title: innerText
  }} {...elementProps} {...rest}>
      {singleInput}
      {label}
    </EuiButtonDisplay>;
};
EuiButtonGroupButton.propTypes = {
  /**
     * Each option must have a unique `id` for maintaining selection
     */
  id: PropTypes.string.isRequired,

  /**
     * Each option must have a `label` even for icons which will be applied as the `aria-label`
     */
  label: PropTypes.node.isRequired,
  isDisabled: PropTypes.bool,

  /**
     * The value of the radio input for 'single'.
     */

  /**
     * The value of the radio input.
     */
  value: PropTypes.oneOfType([PropTypes.any, PropTypes.string]),

  /**
     * The type of the underlying HTML button
     */
  type: PropTypes.oneOf(["button", "submit", "reset"]),

  /**
     * Any `type` accepted by EuiIcon
     */
  iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "analyzeEvent", "annotation", "apmApp", "apmTrace", "apps", "appSearchApp", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "compute", "console", "consoleApp", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "currency", "cut", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentEdit", "documents", "dot", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "fullScreen", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "lensApp", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "pin", "pinFilled", "pipelineApp", "play", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "timeline", "timelionApp", "training", "trash", "upgradeAssistantApp", "uptimeApp", "unfold", "unlink", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]),

  /**
     * Can only be one side `left` or `right`
     */
  iconSide: PropTypes.oneOf(["left", "right"]),
  isLoading: PropTypes.bool,

  /**
     * Object of props passed to the <span/> wrapping the content's text/children only (not icon)
     */
  textProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    ref: PropTypes.any,
    "data-text": PropTypes.string
  }),
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
     * Element to display based on single or multi
     */
  element: PropTypes.oneOf(["button", "label"]).isRequired,

  /**
     * Styles the selected button to look selected (usually with `fill`)
     */
  isSelected: PropTypes.bool,

  /**
     * Name of the whole group for 'single'.
     */
  name: PropTypes.string,

  /**
     * Inherit from EuiButtonGroup
     */
  color: PropTypes.oneOf(["primary", "secondary", "warning", "danger", "ghost", "text"]).isRequired,

  /**
     * Inherit from EuiButtonGroup
     */
  size: PropTypes.oneOf(["s", "m", "compressed"]).isRequired,

  /**
     * Inherit from EuiButtonGroup
     */
  isIconOnly: PropTypes.bool.isRequired,

  /**
     * Inherit from EuiButtonGroup
     */
  onChange: PropTypes.func.isRequired
};

try {
  EuiButtonGroupButton.__docgenInfo = {
    description: '',
    displayName: 'EuiButtonGroupButton',
    methods: [],
    props: {
      id: {
        defaultValue: null,
        description: 'Each option must have a unique `id` for maintaining selection',
        name: 'id',
        parent: {
          fileName: 'app/src/components/button/button_group/button_group.tsx',
          name: 'EuiButtonGroupOptionProps'
        },
        required: true,
        type: {
          name: 'string'
        }
      },
      label: {
        defaultValue: null,
        description: 'Each option must have a `label` even for icons which will be applied as the `aria-label`',
        name: 'label',
        parent: {
          fileName: 'app/src/components/button/button_group/button_group.tsx',
          name: 'EuiButtonGroupOptionProps'
        },
        required: true,
        type: {
          name: 'ReactNode'
        }
      },
      isDisabled: {
        defaultValue: null,
        description: '',
        name: 'isDisabled',
        parent: {
          fileName: 'app/src/components/button/button_group/button_group.tsx',
          name: 'EuiButtonGroupOptionProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      value: {
        defaultValue: null,
        description: 'The value of the radio input.\nThe value of the radio input for \'single\'.',
        name: 'value',
        parent: {
          fileName: 'app/src/components/button/button_group/button_group.tsx',
          name: 'EuiButtonGroupOptionProps'
        },
        required: false,
        type: {
          name: 'any'
        }
      },
      type: {
        defaultValue: {
          value: 'button'
        },
        description: 'The type of the underlying HTML button',
        name: 'type',
        parent: {
          fileName: 'app/src/components/button/button_group/button_group.tsx',
          name: 'EuiButtonGroupOptionProps'
        },
        required: false,
        type: {
          name: 'enum',
          raw: '"button" | "submit" | "reset"',
          value: [{
            value: '"button"'
          }, {
            value: '"submit"'
          }, {
            value: '"reset"'
          }]
        }
      },
      iconType: {
        defaultValue: null,
        description: 'Any `type` accepted by EuiIcon',
        name: 'iconType',
        parent: {
          fileName: 'app/src/components/button/button_content.tsx',
          name: 'EuiButtonContentProps'
        },
        required: false,
        type: {
          name: 'IconType'
        }
      },
      iconSide: {
        defaultValue: null,
        description: 'Can only be one side `left` or `right`',
        name: 'iconSide',
        parent: {
          fileName: 'app/src/components/button/button_content.tsx',
          name: 'EuiButtonContentProps'
        },
        required: false,
        type: {
          name: 'enum',
          raw: '"left" | "right"',
          value: [{
            value: '"left"'
          }, {
            value: '"right"'
          }]
        }
      },
      isLoading: {
        defaultValue: null,
        description: '',
        name: 'isLoading',
        parent: {
          fileName: 'app/src/components/button/button_content.tsx',
          name: 'EuiButtonContentProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      textProps: {
        defaultValue: null,
        description: 'Object of props passed to the <span/> wrapping the content\'s text/children only (not icon)',
        name: 'textProps',
        parent: {
          fileName: 'app/src/components/button/button_content.tsx',
          name: 'EuiButtonContentProps'
        },
        required: false,
        type: {
          name: 'HTMLAttributes<HTMLSpanElement> & CommonProps & { ref?: Ref<HTMLSpanElement>; \'data-text\'?: string; }'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      element: {
        defaultValue: {
          value: 'button'
        },
        description: 'Element to display based on single or multi',
        name: 'element',
        parent: undefined,
        required: false,
        type: {
          name: 'enum',
          raw: '"button" | "label"',
          value: [{
            value: '"button"'
          }, {
            value: '"label"'
          }]
        }
      },
      isSelected: {
        defaultValue: {
          value: 'false'
        },
        description: 'Styles the selected button to look selected (usually with `fill`)',
        name: 'isSelected',
        parent: undefined,
        required: false,
        type: {
          name: 'boolean'
        }
      },
      name: {
        defaultValue: null,
        description: 'Name of the whole group for \'single\'.',
        name: 'name',
        parent: undefined,
        required: false,
        type: {
          name: 'string'
        }
      },
      color: {
        defaultValue: null,
        description: 'Inherit from EuiButtonGroup',
        name: 'color',
        parent: undefined,
        required: true,
        type: {
          name: 'enum',
          raw: 'ButtonColor',
          value: [{
            value: '"text"'
          }, {
            value: '"secondary"'
          }, {
            value: '"danger"'
          }, {
            value: '"warning"'
          }, {
            value: '"ghost"'
          }, {
            value: '"primary"'
          }]
        }
      },
      size: {
        defaultValue: null,
        description: 'Inherit from EuiButtonGroup',
        name: 'size',
        parent: undefined,
        required: true,
        type: {
          name: 'enum',
          raw: '"s" | "m" | "compressed"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"compressed"'
          }]
        }
      },
      isIconOnly: {
        defaultValue: null,
        description: 'Inherit from EuiButtonGroup',
        name: 'isIconOnly',
        parent: undefined,
        required: true,
        type: {
          name: 'boolean'
        }
      },
      onChange: {
        defaultValue: null,
        description: 'Inherit from EuiButtonGroup',
        name: 'onChange',
        parent: undefined,
        required: true,
        type: {
          name: '((id: string, value?: any) => void) | ((id: string) => void)'
        }
      }
    },
    extendedInterfaces: ['EuiButtonGroupOptionProps', 'EuiButtonContentProps', 'CommonProps']
  };
} catch (e) {}